/* eslint-disable react/jsx-props-no-spreading */
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import LegendsBanner from '@cont/Banner/LegendsBanner';
import {
  isClassFeed,
  isEducatorFeed,
  isBatchFeed,
  isTopicFeed,
  isTestSeriesFeed,
  isGenericBanner,
  isPreActivationBlock,
  isSubjectFeed,
  isLiveMentorshipFeed,
  isLessonFeed,
  isCompeteFeed
} from '@utils/feedTypeHelper';
import { feedblockTypes } from '@constants/index';
import { Spacings } from '@styles/index';
import useWindowSize from '@hooks/useWindowSize';
import Dynamic from '@base/Dynamic/Dynamic';
import CompeteAwarenessBanner from '@cont/Compete/Browse/AwarenessBanner';
import EducatorFeedBlock from './EducatorFeedBlock';
import ClassFeedBlock from './ClassFeedBlock';
import TopicFeedBlock from './TopicFeedBlock';
import TestSeriesFeedBlock from './TestSeriesFeedBlock';
import GenericBanner from '../PreSubscribed/GenericBanner';
import BatchFeedBlock from './BatchFeedBlock';
import ActivateSubscription from './ActivateSubscription';
import PopularSubjects from './PopularSubjects';
import LiveMentorshipFeedBlock from './LiveMentorshipFeedBlock';
import LessonsFeedBlock from './LessonsFeedBlock';

const CarouselFeedBlock = Dynamic(() => import('./CarouselFeedBlock'));

// PlusHome, FreeCourse, SpecialClasses

const exhaustiveFeedProps = {
  educator: {},
  class: {},
  batch: {},
  topic: {},
  testSeries: {}
};

const LegendsBannerWrapper = styled.div`
  grid-column: span 18;
  height: 265px;
  margin-top: ${Spacings.SPACING_2B};
  margin-bottom: ${Spacings.SPACING_9B};
`;

const GenericBannerWrapper = styled.div`
  grid-column: span 18;
  margin-top: ${Spacings.SPACING_9B};
  margin-bottom: ${Spacings.SPACING_9B};
`;

const FeedTypeBlock = ({
  feedBlock,
  goalSlug,
  goalUID,
  goal,
  enroll,
  viewAllLink,
  viewAllHref,
  type,
  feedProps,
  dataAnalytics,
  lastPrimarySource,
  openExternalAppActivation,
  index
}) => {
  const feedType = feedBlock.extraBlockinfo?.entity_type;

  const allFeedProps = {
    ...exhaustiveFeedProps,
    ...feedProps
  };
  const { isMobile } = useWindowSize(); // commenting for mweb temporarily.. Need to be fixed for all types of banners..

  if (feedBlock.type === feedblockTypes.CAROUSEL_FEED && !isMobile) {
    return (
      <CarouselFeedBlock
        feedBlock={feedBlock}
        block={feedBlock}
        goalSlug={goalSlug}
        goalUID={goalUID}
        dataAnalytics={dataAnalytics}
        lastPrimarySource={lastPrimarySource}
        enroll={enroll}
        {...allFeedProps.batch}
      />
    );
  }
  if (
    feedBlock.type === feedblockTypes.LEGENDS_ON_UNACADEMY ||
    feedBlock.type === feedblockTypes.BANNER // SpecialClassLanding
  ) {
    const legendsCourse = feedBlock.courses[0];

    return (
      <LegendsBannerWrapper>
        <LegendsBanner
          course={legendsCourse}
          bannerType={feedBlock.type === feedblockTypes.BANNER ? 0 : 1} // SpecialClassLanding
          enroll={enroll}
        />
      </LegendsBannerWrapper>
    );
  }

  if (feedBlock.type === feedblockTypes.MY_DOWNLOADS) return null; // SpecialClasmjsLanding

  if (isLiveMentorshipFeed(feedBlock.type)) {
    return <LiveMentorshipFeedBlock feedBlock={feedBlock} goalUID={goalUID} />;
  }

  if (isSubjectFeed(feedType)) {
    return <PopularSubjects feedBlock={feedBlock} />;
  }

  if (isEducatorFeed(feedType)) {
    return (
      <EducatorFeedBlock
        educatorFeedBlock={feedBlock}
        viewAllLink={viewAllLink}
        viewAllHref={viewAllHref}
        dataAnalytics={dataAnalytics}
        lastPrimarySource={lastPrimarySource}
        index={index}
        {...allFeedProps.educator}
      />
    );
  }
  if (isClassFeed(feedType))
    return (
      <ClassFeedBlock
        viewAllLink={viewAllLink}
        viewAllHref={viewAllHref}
        classFeedBlock={feedBlock}
        type={type}
        dataAnalytics={dataAnalytics}
        lastPrimarySource={lastPrimarySource}
        index={index}
        goalUID={goalUID}
        {...allFeedProps.class}
      />
    );

  if (isLessonFeed(feedType))
    return (
      <LessonsFeedBlock
        viewAllLink={viewAllLink}
        viewAllHref={viewAllHref}
        lessonsFeedBlock={feedBlock}
        type={type}
        dataAnalytics={dataAnalytics}
        lastPrimarySource={lastPrimarySource}
        index={index}
        goalUID={goalUID}
        {...allFeedProps.class}
      />
    );

  if (isBatchFeed(feedType))
    return (
      <BatchFeedBlock
        feedBlock={feedBlock}
        goalSlug={goalSlug}
        goalUID={goalUID}
        dataAnalytics={dataAnalytics}
        lastPrimarySource={lastPrimarySource}
        index={index}
        {...allFeedProps.batch}
      />
    );

  if (isTopicFeed(feedType))
    return (
      <TopicFeedBlock
        goalSlug={goalSlug}
        goalUID={goalUID}
        topicFeedBlock={feedBlock}
        goal={goal}
        viewAllLink={viewAllLink}
        viewAllHref={viewAllHref}
        type={type}
        topicType={feedBlock.extraBlockinfo?.type}
        dataAnalytics={dataAnalytics}
        lastPrimarySource={lastPrimarySource}
        index={index}
        {...allFeedProps.topic}
      />
    );

  if (isTestSeriesFeed(feedType))
    return (
      <TestSeriesFeedBlock
        goalSlug={goalSlug}
        goalUID={goalUID}
        dataAnalytics={dataAnalytics}
        testSeriesFeedBlock={feedBlock}
        lastPrimarySource={lastPrimarySource}
        index={index}
        {...allFeedProps.testSeries}
        feedBlocktype={type}
      />
    );

  if (isGenericBanner(feedBlock.type))
    return (
      <GenericBannerWrapper>
        <GenericBanner block={feedBlock} />
      </GenericBannerWrapper>
    );

  if (isPreActivationBlock(feedBlock.type)) {
    return (
      <ActivateSubscription
        feedBlock={feedBlock}
        openExternalAppActivation={openExternalAppActivation}
        isBrowseBlock={
          feedBlock.type === 'external_subscription_postactivation'
        }
      />
    );
  }

  if (isCompeteFeed(feedBlock.type)) {
    return <CompeteAwarenessBanner block={feedBlock} />;
  }

  return null;
};

export default FeedTypeBlock;
FeedTypeBlock.propTypes = {
  feedBlock: PropTypes.objectOf(PropTypes.unknown).isRequired,
  goal: PropTypes.objectOf(PropTypes.unknown),
  goalSlug: PropTypes.string.isRequired,
  goalUID: PropTypes.string.isRequired,
  enroll: PropTypes.func,
  viewAllLink: PropTypes.string,
  viewAllHref: PropTypes.string,
  type: PropTypes.string,
  feedProps: PropTypes.objectOf(PropTypes.unknown),
  dataAnalytics: PropTypes.string,
  lastPrimarySource: PropTypes.string,
  openExternalAppActivation: PropTypes.func,
  index: PropTypes.number
};

FeedTypeBlock.defaultProps = {
  goal: {},
  enroll: null,
  viewAllLink: '',
  viewAllHref: '',
  type: '',
  feedProps: exhaustiveFeedProps,
  dataAnalytics: '',
  lastPrimarySource: '',
  openExternalAppActivation: null,
  index: 0
};
